import { CartUpdateEvent, CartGroupedSections, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Free electrolyte gift.
 * Adds 1 x gift variant once the cart (excluding the gift) reaches the threshold,
 * removes it when the cart drops below. Shopify's Buy X Get Y automatic discount
 * makes it free -- this file only adds and removes the line.
 */

const GIFT_VARIANT_ID = 54331190083907;
const THRESHOLD = 4999; // pence, £49.99 inclusive
const SOURCE_ID = 'free-gift';
const DEBUG = true; // set to false once it is working

const root = window.Shopify?.routes?.root || '/';
const log = (...args) => DEBUG && console.log('[free-gift]', ...args);

let busy = false;
let soldOut = false;        // /cart/add.js refused -- stop retrying this page load
let discountBroken = false; // gift added but not discounted -- stop retrying

const post = (path, body) =>
  fetch(`${root}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

const getCart = () =>
  fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

const isGift = (item) => item.variant_id === GIFT_VARIANT_ID;

const eligibleTotal = (cart) =>
  cart.items.reduce((sum, item) => (isGift(item) ? sum : sum + item.final_line_price), 0);

/** Every cart section on the page: components that answer the event, plus a DOM sweep. */
function cartSectionIds() {
  const ids = new Set();
  const collected = [];
  document.dispatchEvent(new CartGroupedSections(collected));
  collected.forEach((id) => id && ids.add(id));
  document
    .querySelectorAll('cart-items-component[data-section-id], [data-section-id] cart-items-component')
    .forEach((el) => {
      const id = el.dataset.sectionId || el.closest('[data-section-id]')?.dataset.sectionId;
      if (id) ids.add(id);
    });
  return [...ids];
}

async function refreshCartUI(cart) {
  const ids = cartSectionIds();
  log('re-rendering sections', ids);
  await Promise.all(
    ids.map((id) =>
      sectionRenderer.renderSection(id, { cache: false }).catch((e) => log('render failed', id, e))
    )
  );
  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE_ID, {
      itemCount: cart.item_count,
      source: SOURCE_ID,
    })
  );
}

async function sync() {
  if (busy) return;
  busy = true;

  try {
    const cart = await getCart();
    const gift = cart.items.find(isGift);
    const total = eligibleTotal(cart);
    const qualifies = total >= THRESHOLD;
    log('cart', { total, qualifies, hasGift: !!gift, soldOut, discountBroken });

    let changed = false;

    if (gift && gift.final_line_price > 0) {
      // Shopify may not have applied the automatic discount yet. Wait and look again
      // before concluding anything -- a transient must not disable the offer.
      await delay(DISCOUNT_GRACE_MS);
      const recheck = await getCart();
      const giftAgain = recheck.items.find(isGift);

      if (giftAgain && giftAgain.final_line_price > 0) {
        discountFailures += 1;
        await post('cart/change.js', { id: giftAgain.key, quantity: 0 });
        console.warn(
          `[free-gift] discount did not apply (${discountFailures}/${MAX_DISCOUNT_FAILURES}) -- gift removed`
        );
        if (discountFailures >= MAX_DISCOUNT_FAILURES) discountBroken = true;
        changed = true;
      } else {
        log('discount applied after grace period');
        discountFailures = 0;
      }
    } else if (qualifies && !gift && !soldOut && !discountBroken) {
      const res = await post('cart/add.js', {
        items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: { _free_gift: 'true' } }],
      });
      log('add gift ->', res.status);
      if (res.ok) {
        changed = true;
      } else {
        soldOut = true; // 422 = out of stock
      }
    } else if (!qualifies && gift) {
      await post('cart/change.js', { id: gift.key, quantity: 0 });
      log('removed gift (below threshold)');
      changed = true;
    } else if (gift && gift.quantity > 1) {
      await post('cart/change.js', { id: gift.key, quantity: 1 });
      changed = true;
    }

    if (changed) {
      const updated = await getCart();
      await refreshCartUI(updated);
    }
  } catch (error) {
    console.warn('[free-gift]', error);
  } finally {
    busy = false;
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.sourceId === SOURCE_ID) return; // ignore our own dispatch
  log('cart:update seen from', event.detail?.sourceId);
  sync();
});

sync();
